import { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import { StreamChat } from 'stream-chat';
import { GET_CHAT_TOKEN } from 'api/cb-chat/query';
import { GetChatToken } from 'api/cb-chat/types/GetChatToken';
import { useAppContext, useGetCurrUser } from 'hooks';
import { isNil } from 'lodash';

const STREAM_CHAT_API_KEY = process.env.NEXT_PUBLIC_STREAM_CHAT_API_KEY ?? '';

type UseStreamChatClientReturn = {
  client: StreamChat | null;
  userId: string | null;
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
};

let sharedClient: StreamChat | null = null;
let sharedConnectionPromise: Promise<StreamChat> | null = null;

type StreamChatClientWithUnread = StreamChat & {
  user?: {
    total_unread_count?: number;
  };
  on: (
    callback: (event: {
      type?: string;
      total_unread_count?: number;
      me?: {
        total_unread_count?: number;
      };
    }) => void
  ) => {
    unsubscribe: () => void;
  };
};

const getStreamClient = (): StreamChat => {
  if (!sharedClient) {
    sharedClient = StreamChat.getInstance(STREAM_CHAT_API_KEY);
  }

  return sharedClient;
};

const connectStreamUser = async ({
  userId,
  userName,
  userImage,
  chatToken,
}: {
  userId: string;
  userName: string;
  userImage: string;
  chatToken: string;
}): Promise<StreamChat> => {
  const streamClient = getStreamClient();

  if (streamClient.userID === userId) {
    return streamClient;
  }

  if (sharedConnectionPromise) {
    return sharedConnectionPromise;
  }

  sharedConnectionPromise = (async () => {
    if (streamClient.userID && streamClient.userID !== userId) {
      await streamClient.disconnectUser();
    }

    await streamClient.connectUser(
      {
        id: userId,
        name: userName || userId,
        image: userImage,
      },
      chatToken
    );

    return streamClient;
  })();

  try {
    return await sharedConnectionPromise;
  } finally {
    sharedConnectionPromise = null;
  }
};

export const useStreamChatClient = (): UseStreamChatClientReturn => {
  const { token: contextToken } = useAppContext();
  const { data: userData } = useGetCurrUser();
  const userId = userData?.me?.id ?? null;
  const userName = [userData?.me?.firstName, userData?.me?.lastName]
    .filter(Boolean)
    .join(' ');
  const userImage = userData?.me?.storeDetails?.avatarURL ?? '';

  const [client, setClient] = useState<StreamChat | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { data: tokenData, loading: tokenLoading } = useQuery<GetChatToken>(
    GET_CHAT_TOKEN,
    { skip: !contextToken || !userId }
  );

  const chatToken = tokenData?.getChatToken ?? null;

  useEffect(() => {
    if (!STREAM_CHAT_API_KEY) {
      setIsLoading(false);
      setClient(null);
      setError('Stream Chat is not configured');
      return;
    }

    if (!chatToken || !userId) {
      setIsLoading(true);
      setClient(null);
      setError(null);
      return;
    }

    let cancelled = false;

    const connect = async () => {
      setIsLoading(true);
      setError(null);

      try {
        const streamClient = await connectStreamUser({
          userId,
          userName,
          userImage,
          chatToken,
        });

        if (cancelled) return;

        setClient(streamClient);
      } catch (err: any) {
        if (cancelled) return;

        setClient(null);
        setError('Failed to connect to chat');
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    connect();

    return () => {
      cancelled = true;
    };
  }, [chatToken, userId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!client) {
      setUnreadCount(0);
      return;
    }

    const streamClient = client as StreamChatClientWithUnread;

    setUnreadCount(streamClient.user?.total_unread_count ?? 0);

    const subscription = streamClient.on((event) => {
      const count = event.total_unread_count ?? event.me?.total_unread_count;
      if (!isNil(count)) {
        // Keep the singleton user object in sync so future remounts
        // seed the correct value instead of a stale one.
        if (streamClient.user) {
          streamClient.user.total_unread_count = count;
        }
        setUnreadCount(count);
        return;
      }
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [client]);

  const waitingForToken =
    !!userId && !!contextToken && tokenLoading && !chatToken;
  const isConnecting = isLoading || waitingForToken;

  return {
    client: isConnecting ? null : client,
    userId,
    unreadCount,
    isLoading: isConnecting,
    error,
  };
};
